import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cn from 'classnames';
import { first } from 'lodash';
import moment from 'moment';

import getBookingHistory from 'actions/client/getBookingHistory.action';
import saveClient from 'actions/client/saveClient.action';
import { createDepositBookingVersion } from 'actions/payment/beCreateDeposit.action';
import { feSidebarDetourReturn } from 'actions/ui.actions';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import SalonSpinner from 'components/generic/SalonSpinner.component';
import { selectSidebarDetour } from 'selectors/sidebar.selectors';
import { selectEditedClientTags } from 'selectors/simple';
import { insertAt } from 'utils/arrays/array.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { fromPropsToClientId } from 'utils/converters/props/bl.util';
import { fromPropsToPath, fromPropsToPathSegments } from 'utils/converters/props/path.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { nowAsMoment, nowAsTimestamp } from 'utils/date/now.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import { bitA, bitO } from 'utils/logic.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import { clearFutureBookingData } from 'booking/actions';
import BackButton from 'booking/components/common/bw-header/BackButton';
import HeaderWrapper from 'booking/components/common/bw-header/HeaderWrapper';
import { fetchClient, getNoShows, getScheduledBooking, removeEditClient } from 'clientProfile/actions';
import AddCreditOnAccount from 'clientProfile/components/BasicProfile/AddCreditOnAccount';
import BasicProfile from 'clientProfile/components/BasicProfile/BasicProfile.component';
import EditPatchNotesContainer from 'clientProfile/components/BasicProfile/EditPatchNotes';
import BookingDetailsContainer from 'clientProfile/components/bookingDetails/BookingDetailsContainer';
import BookingHistoryContainer from 'clientProfile/components/bookingHistory/BookingHistoryContainer';
import ScheduledBookingContainer from 'clientProfile/components/scheduledBooking/ScheduledBookingContainer';
import { fetchClientDepositDetails, sendRefundEmail } from 'settings/balance-sheet/actions';
import { trackEvent } from 'tracking/actions';

import CN from './ClientProfile.module.scss';

const CTAG = 'ClientProfile';
const L = logger(CTAG);

const MIN_PAGE = 1;
const CONTAINERS = Object.freeze({
  Basic: 0b1,
  PatchTest: 0b10,
  EditDetails: 0b100,
  BookingHistory: 0b1000,
  ScheduledBooking: 0b10000,
  BookingDetails: 0b100000,
  AddCredit: 0b1000000,
});

const DEFAULT_CONTAINERS = bitO(CONTAINERS.Basic, CONTAINERS.BookingHistory, CONTAINERS.ScheduledBooking);

class ClientProfile extends React.Component {
  constructor(props) {
    super(props);

    const clientList = this.props?.currentSalon?.clientList;
    const parsedId = parseInt(this.props?.match?.params?.client_id, 10);

    this.state = {
      client: isFullArray(clientList) ? first(clientList.filter((cli) => cli.id === parsedId)) : void 1,
      loaded: false,
      requested: false,
      autoSave: { sms: { isSaving: false, isSaved: false }, marketing: { isSaving: false, isSaved: false } },
      containersShown: DEFAULT_CONTAINERS,
      currentBackBtn: alwaysNoop,
      currentReviewedBooking: null,
      isLoading: false,
      currentPage: MIN_PAGE,
    };

    this.showMoreButtonRef = createRef();
  }

  UNSAFE_componentWillMount() {
    const { client, salonId, clearFutureBookingData, fetchClientDepositDetails } = this.props;

    const clientIdFromParams = fromPropsToClientId(this.props);
    const clientIdFromProps = client?.id;
    const clientId = fromAnyToInteger(clientIdFromParams || clientIdFromProps);

    clearFutureBookingData();

    if (clientId) {
      fetchClientDepositDetails(salonId, clientId);
      this.loadAllData(clientId);
    } else {
      L.warn('componentWillMount()', 'no clientId:', clientId, { clientIdFromParams, clientIdFromProps });
    }
    this.goBackClientProfile();
  }

  componentDidMount() {
    const scroller = window.document.getElementById('client_profile_scoller');

    if (this.state.currentScrollPosition && scroller && scroller.scrollTo) {
      scroller.scrollTo(0, this.state.currentScrollPosition);
    }
    if (this.props.location && this.props.location.state) {
      if (this.props.location.state.fromNoPatch) {
        this.goToPatchView();
      }
    }

    this.fetchScheduledBookings({ page: MIN_PAGE });
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { client: clientFromPrev, currentSalon } = this.props;
    const clientFromNext = nextProps.client;

    // FIXME: directly changing the autoSave object from state
    const { autoSave } = this.state;

    // if it is in loading state, and we receive a client it is loaded
    const newClientId = fromAnyToInteger(fromPropsToClientId(nextProps));
    const oldClientId = fromAnyToInteger(fromPropsToClientId(this.props));

    if (clientFromNext && newClientId === clientFromNext.id) {
      this.setState({ client: Object.assign({}, clientFromNext), loaded: true, requested: false });
    }

    if (newClientId !== oldClientId || (clientFromPrev && newClientId !== clientFromPrev.id)) {
      // if the client is different from the client we have, we should look for the new one
      // and fetch its new data
      if (newClientId && !this.state.requested) {
        this.loadAllData(newClientId);
      }

      const client = first(currentSalon.clientList.filter(({ id }) => id === newClientId));
      if (client) {
        this.setState({ client: { ...client }, loaded: false, containersShown: DEFAULT_CONTAINERS });
      }
    } else if (clientFromPrev && newClientId === clientFromPrev.id) {
      // if the client is the same as the existing one we are checking if the changed data is not from the
      // checkboxes. If it is from the checkboxes we need to change the "Saving..." and "Saved!" states for the labels

      if (this.state.autoSave.sms.isSaving && clientFromPrev.notifications !== clientFromNext.notifications) {
        autoSave.sms.isSaving = false;
        autoSave.sms.isSaved = true;
      }

      if (
        this.state.autoSave.marketing.isSaving &&
        clientFromPrev.requested_opt_out !== clientFromNext.requested_opt_out
      ) {
        autoSave.marketing.isSaving = false;
        autoSave.marketing.isSaved = true;
      }

      this.setState({ autoSave });
    }

    if (nextProps.historyBookings && nextProps.historyBookings.length) {
      const interval = setInterval(() => {
        const historyBookingDiv = document.querySelector('.bh');
        if (historyBookingDiv && historyBookingDiv.firstChild) {
          clearInterval(interval);
        }
      }, 500);
    }
  }

  componentWillUnmount() {
    this.props.removeEditClient();
  }

  fetchScheduledBookings = (options) => {
    const { client, salonId } = this.props;

    const { page } = options;

    const clientIdFromParams = fromPropsToClientId(this.props);
    const clientIdFromProps = client && client.id;
    const clientId = fromAnyToInteger(clientIdFromParams || clientIdFromProps);

    L.unignore(
      'fetchScheduledBookings()',
      'getScheduledBooking:',
      this.props.getScheduledBooking(salonId, clientId, page),
    );

    this.setState({ currentPage: page });
  };

  loadAllData = (clientId) => {
    const { currentSalon, getBookingHistory, getNoShows, getClient, clearFutureBookingData } = this.props;
    const salonId = currentSalon.id;

    L.unignore('loadAllData()', 'getClient:', getClient(salonId, clientId));
    L.unignore('loadAllData()', 'clearFutureBookingData:', clearFutureBookingData());
    L.unignore('loadAllData()', 'getNoShows:', getNoShows(salonId, clientId));
    L.unignore('loadAllData()', 'getBookingHistory:', getBookingHistory(salonId, clientId));

    this.setState({ requested: true });
  };

  loadMoreBookingHistory = async () => {
    this.setState({ isLoading: true });
    const { client, getBookingHistory, currentSalon, lastTimeStamp } = this.props;
    const clientId = client && client.id;
    const salonId = currentSalon.id;

    if (clientId) {
      L.unignore('getMoreBookingHistory()', 'getBookingHistory:', getBookingHistory(salonId, clientId, lastTimeStamp));
      await getBookingHistory(salonId, clientId, lastTimeStamp);
    }

    this.setState({ isLoading: false }, () => {
      if (this.showMoreButtonRef.current) {
        this.showMoreButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  customCloseFunction = () => {
    if (this.props.location.pathname.indexOf('appointment') > 0) {
      // opened from EditBooking
      this.goBack();
    } else {
      // opened from three dots/Client Profile
      this.props.history.push(this.props.location.pathname.split('/client')[0]);
    }
  };

  autosaveClientSMS = (notifications) => {
    const { client: clientFromState, autoSave } = this.state;
    const { client: clientFromProps } = this.props;

    if (clientFromState) {
      this.setState({ client: { ...clientFromState, notifications }, autoSave });
      this.saveClientData({
        notifications,
        id: clientFromState.id,
        email: clientFromProps.email,
        phone: clientFromProps.phone,
        salon_id: clientFromState.salon_id,
      });
    }
  };

  saveMarketing = (requestedOptOut) => {
    const { client: clientFromState } = this.state;
    const { client: clientFromProps, trackEvent } = this.props;

    trackEvent('toggle_optin');

    if (clientFromState) {
      this.setState({ client: { ...clientFromState, requested_opt_out: requestedOptOut } });
      this.saveClientData({
        requested_opt_out: requestedOptOut,
        id: clientFromState.id,
        email: clientFromProps.email,
        phone: clientFromProps.phone,
        salon_id: clientFromState.salon_id,
      });
    }
  };

  saveClientData = (client) => {
    const { saveClient, currentSalon } = this.props;
    const salonId = currentSalon.id;

    L.unignore('saveClientData()', 'saveClient:', saveClient(salonId, client));
  };

  saveNewDetails = (client, savePatchData) => {
    const { saveClient, currentSalon } = this.props;
    const salonId = currentSalon.id;

    if (savePatchData && !client.patch_test_date) {
      // FIXME: mutates shared data
      // eslint-disable-next-line no-param-reassign
      client.patch_test_date = nowAsMoment();
      saveClient(salonId, client);
    }

    L.unignore('saveNewDetails()', 'saveClient:', saveClient(salonId, client));
    this.setState({ client });
  };

  goToAddCredit = () => void this.setState({ containersShown: CONTAINERS.AddCredit });

  goToPatchView = () => {
    this.setState({ containersShown: CONTAINERS.PatchTest, currentBackBtn: this.goBackClientProfile });
    this.props.trackEvent('patch_test_Add');
  };

  goToEditDetails = (from) => {
    const fromScreen = from || '';
    const segments = fromPropsToPathSegments(this.props);
    const index = segments.indexOf('client') + 1;
    const pathname = insertAt({ index, what: 'edit', array: segments }).join('/');
    navFor(CTAG, 'goToEditDetails()', this.props).push({ pathname, state: { fromScreen } });
  };

  goBackClientProfile = () => {
    if (fromPropsToPath(this.props).includes('appointment')) {
      this.setState({ containersShown: DEFAULT_CONTAINERS, currentBackBtn: this.goBackClientProfile });
    } else {
      this.setState({ containersShown: DEFAULT_CONTAINERS, currentBackBtn: null });
    }
  };

  goToViewDeposits = (clientId) => {
    const { history } = this.props;
    const pathname = fromPropsToPath(this.props);
    const weekView = document.querySelector('.W-view');

    if (weekView) {
      const depositPath = `${pathname.split('week')[0]}deposits`;
      history.push({ pathname: depositPath, state: { clientId } });
    } else {
      const depositPath = `${pathname.split('day')[0]}deposits`;
      history.push({ pathname: depositPath, state: { clientId } });
    }
  };

  goToViewDetails = (appointment) => {
    this.setState({
      containersShown: CONTAINERS.BookingDetails,
      currentBackBtn: this.goBackClientProfile,
      currentReviewedBooking: appointment,
    });
  };

  goToSchedulerAndOpenModal = (appointmentId, dateToSwitchTo) => {
    const {
      history,
      currentSalon: { id: salonId },
    } = this.props;
    const path = fromPropsToPath(this.props);

    const day = moment(dateToSwitchTo).format('YYYYMMDD');
    const definedTimestamp = nowAsTimestamp();

    const pathname = path.indexOf('/Week/') < 0 ? `/salon/${salonId}/day/${day}/` : first(path.split('/client/'));
    const state = { showModalAction: { appointmentId, dateToSwitchTo, definedTimestamp } };

    history.push({ pathname, state });
  };

  goBack = () => {
    const pathname = fromPropsToPath(this.props);
    if (!pathname.includes('appointment') && !pathname.includes('client')) return void this.state.currentBackBtn();

    if (this.state.containersShown === DEFAULT_CONTAINERS) return void this.props.history.goBack();

    this.setState({ containersShown: DEFAULT_CONTAINERS });
  };

  handleNextClick = () => void this.fetchScheduledBookings({ page: this.state.currentPage + 1 });
  handlePrevClick = () => void this.fetchScheduledBookings({ page: this.state.currentPage - 1 });

  handleCloseV2 = (...args) => {
    if (this.props.isSidebarDetour) this.props.feSidebarDetourReturn();
    this.customCloseFunction(...args);
  };

  handleBackV2 = (...args) => {
    if (this.props.isSidebarDetour) this.props.feSidebarDetourReturn();
    this.goBack(...args);
  };

  render() {
    const {
      autoSave,
      currentBackBtn,
      loaded,
      containersShown,
      currentReviewedBooking,
      client: clientFromState,
      isLoading,
      currentPage,
    } = this.state;

    const {
      client: clientFromProps,
      scheduledBookings,
      isLoadingScheduledBooking,
      remainingHistoryBookings,
      bookingStatusReport,
      clientTags,
      clientDepositData,
      salonId,
      fetchClientDepositDetails,
      isFetchingClient,
      historyBookings,
      sendRefundEmail,
      createDepositBackEnd,
      currentSalon,
    } = this.props;

    const isSmallStickyHeader = bitA(containersShown, CONTAINERS.BookingDetails) && currentReviewedBooking;
    const scrollerClasses = cn({
      BookingWindow: true,
      'sticky-header-small-hight': isSmallStickyHeader,
      'sticky-header': !isSmallStickyHeader,
    });

    const isWithBookingOrHistory =
      (scheduledBookings && scheduledBookings.length > 0) || (historyBookings && historyBookings.length > 0);
    const basicProfileContainerClasses = cn({
      ' ': isWithBookingOrHistory,
      'client-has-no-feature-data': !isWithBookingOrHistory,
    });

    const isPrevEnabled = currentPage <= MIN_PAGE;
    const isNextEnabled = !scheduledBookings || scheduledBookings.length < 5;

    return (
      <section data-bem="ClientProfile" className="booking-steps">
        <div className={scrollerClasses} id="client_profile_scoller">
          <HeaderWrapper>
            {containersShown && currentBackBtn ? (
              <BackButton onClick={this.handleBackV2} />
            ) : (
              <div
                data-bem="ClientProfile__go-back-arrow"
                className={CN.icon}
                role="button"
                onClick={this.handleCloseV2}
              >
                <Icon variant="arrowRight" variantInvertDark width="lg" />
              </div>
            )}
          </HeaderWrapper>

          {(isLoadingScheduledBooking || !loaded) && (
            <div className={CN.spinnerContainer}>
              <SalonSpinner variant="dark" />
              <span className={CN.spinnerText}>
                {isLoadingScheduledBooking ? 'Loading bookings...' : 'Loading client'}
              </span>
            </div>
          )}
          {loaded && !isLoadingScheduledBooking && clientFromState && bitA(containersShown, CONTAINERS.Basic) ? (
            <div className="container-under-sticky-header">
              <BasicProfile
                cssCheckForFeatureData={basicProfileContainerClasses}
                client={clientFromState}
                remainingHistoryBookings={remainingHistoryBookings}
                clientTags={clientTags}
                bookingStatusReport={bookingStatusReport}
                isSavingSms={autoSave?.sms?.isSaving}
                onToggleSms={this.autosaveClientSMS}
                onToggleMarketing={this.saveMarketing}
                goToPatchView={this.goToPatchView}
                goToEditDetails={this.goToEditDetails}
                goToAddCredit={this.goToAddCredit}
                saveClient={this.saveNewDetails}
                clientDepositData={clientDepositData}
                goToViewDeposits={this.goToViewDeposits}
              />
            </div>
          ) : (
            ''
          )}

          {bitA(containersShown, CONTAINERS.PatchTest) ? (
            <EditPatchNotesContainer
              isFetching={isFetchingClient}
              client={clientFromProps}
              goBackClientProfile={this.goBackClientProfile}
              saveClient={this.saveNewDetails}
            />
          ) : (
            ''
          )}

          {bitA(containersShown, CONTAINERS.AddCredit) ? (
            <AddCreditOnAccount
              clientId={clientFromProps && clientFromProps.id ? clientFromProps.id : 0}
              createDepositBackEnd={createDepositBackEnd}
              fetchClientDepositDetails={fetchClientDepositDetails}
              goBackClientProfile={this.goBackClientProfile}
              isFetching={isFetchingClient}
              salonId={salonId}
            />
          ) : (
            ''
          )}

          {bitA(containersShown, CONTAINERS.ScheduledBooking) && scheduledBookings?.length && clientFromState && (
            <div data-bem="ClientProfile__ScheduledBooking">
              <ScheduledBookingContainer
                data={scheduledBookings}
                onScheduledAppointmentClick={this.goToSchedulerAndOpenModal}
                smsRemindersOn={clientFromState.notifications}
              />
              <div data-bem="ClientProfile__ScheduledBooking-buttons" className={CN.navButtons}>
                <Button
                  tag="ClientProfile__button-prev"
                  variant="outline"
                  width="small"
                  onClick={this.handlePrevClick}
                  disabled={isPrevEnabled || isLoadingScheduledBooking}
                >
                  <span className={CN.buttonSpace}>PREV</span>
                </Button>

                <Button
                  tag="ClientProfile__button-next"
                  variant="outline"
                  width="small"
                  onClick={this.handleNextClick}
                  disabled={isNextEnabled || isLoadingScheduledBooking}
                >
                  <span className={CN.buttonSpace}>NEXT</span>
                </Button>
              </div>
            </div>
          )}

          {bitA(containersShown, CONTAINERS.BookingHistory) &&
          historyBookings &&
          historyBookings.length &&
          clientFromState ? (
            <BookingHistoryContainer
              data={historyBookings.filter(
                // this comment avoids ESLint+Prettier formatting issue
                // filter the bookings to show only those who are not part of scheduled bookings
                (booking) => scheduledBookings.every(({ id }) => id !== booking.id),
              )}
              remainingHistoryBookings={remainingHistoryBookings}
              getMoreBookingHistory={this.loadMoreBookingHistory}
              isLoading={isLoading}
              showMoreButtonRef={this.showMoreButtonRef}
              goToViewDetails={this.goToViewDetails}
              onAppointmentClick={this.goToSchedulerAndOpenModal}
            />
          ) : (
            ''
          )}

          {isSmallStickyHeader && currentReviewedBooking ? (
            <BookingDetailsContainer
              appointment={currentReviewedBooking}
              currentSalon={currentSalon}
              sendRefundEmail={sendRefundEmail}
              onAppointmentClick={this.goToSchedulerAndOpenModal}
              clientId={clientFromProps.id || 0}
            />
          ) : (
            ''
          )}
        </div>
      </section>
    );
  }
}

const ClientProfileHoc = withRouter(
  connect(
    (state) => ({
      isSidebarDetour: selectSidebarDetour(state),
      currentSalon: state?.currentSalon,
      salonId: state?.currentSalon?.id,
      client: state?.clientProfile?.edit_client?.data,
      clientDepositData: state?.booking?.clientDepositData,
      isFetchingClient: state?.clientProfile?.edit_client?.isFetching,
      isAutosavingSMS: state?.clientProfile?.edit_client?.isAutosavingSMS,
      isSMSSaved: state?.clientProfile?.edit_client?.isSMSSaved,
      historyBookings: state?.clientProfile?.historyBookings,
      scheduledBookings: state?.clientProfile?.scheduledBookings,
      isLoadingScheduledBooking: state?.clientProfile?.isFetching,
      bookingStatusReport: state?.clientProfile?.edit_client?.bookingStatusReport,
      clientTags: selectEditedClientTags(state),
      lastShownRepeatId: state?.booking?.lastShownRepeatId,
      lastTimeStamp: state?.clientProfile?.lastTimeStamp,
      remainingHistoryBookings: state?.clientProfile?.remainingHistoryBookings,
    }),
    mapActionsToDispatchToProps({
      getClient: (salonId, clientId) => fetchClient(salonId, { id: clientId }),
      saveClient,
      removeEditClient,
      getNoShows,
      getBookingHistory,
      getScheduledBooking,
      sendRefundEmail,
      trackEvent,

      clearFutureBookingData,
      fetchClientDepositDetails,
      createDepositBackEnd: createDepositBookingVersion,

      feSidebarDetourReturn,
    }),
  )(ClientProfile),
);

export default ClientProfileHoc;
